import React, { useEffect, useState } from 'react';
import { Field, Formik, FormikProps, FieldArray } from 'formik';
import { Persist } from 'formik-persist';
import * as Yup from 'yup';

import { makeStyles, Grid } from '@material-ui/core';
import Fields from 'components/Fields';
import { Button, BoxWrapper } from 'components/Base';
import Customers from './Customers';
import { Action, useAuthIo } from 'providers/AuthProvider/AuthIoProvider';
import { compose } from 'redux';
import { connect } from 'react-redux';
import selectors from 'store/selectors';
import intellifloLogo from '../../../../assets/icons/intelliflo-logo.svg';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  customers: Yup.array().of(
    Yup.object().shape({
      first_name: Yup.string().required('Required'),
      last_name: Yup.string().required('Required')
    })
  )
});

const useStyles = makeStyles({
  form: {
    width: '25rem',
    minHeight: '22rem',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column'
  },
  content: {
    height: '100%',
    flex: 1,
    padding: '1rem'
  },
  btnContainer: {
    width: '100%',
    height: '36px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'flex-end'
  },
  addCustomer: {
    cursor: 'pointer',
    borderRadius: '50px',
    '& span': {
      fontSize: '0.7rem'
    }
  }
});

interface FormValues {
  name: string;
  customers: { first_name: string; last_name: string }[];
}

interface NewCaseProps {
  setCaseInfo?: (caseInfo: any) => void;
  setLinkedCustomersToCase?: (data: any) => void;
  closeModal: Function;
  onSubmit: (values: FormValues) => void;
  setIsImportUserModal: Function;
  importedCustomers: any[];
  setImportedCustomers: Function;
  caseName: string;
  setCaseName: Function;
  advisor: any;
}
const NewCase = ({
  closeModal,
  onSubmit,
  setIsImportUserModal,
  importedCustomers,
  setImportedCustomers,
  caseName,
  setCaseName,
  advisor
}: NewCaseProps) => {
  const classes = useStyles();
  const { isAuthenticated, signIn } = useAuthIo();
  // eslint-disable-next-line
  const [showIoImport, setShowIoImport] = useState(false);
  const [mappedAdvisers, setMappedAdvisers] = useState([] as any);
  const [defaultAdviserId, setDefaultAdviserId] = useState(null as any);

  const close = (reset: Function) => {
    reset();
    localStorage.removeItem('new-case');
    closeModal();
  };

  const IoCheck = () => {
    return (
      !!advisor.organization &&
      !!advisor.organization.crm &&
      advisor.organization.crm.includes('intelliflo')
    );
  };

  useEffect(() => {
    setShowIoImport(IoCheck());
    setMappedAdvisers(
      (advisor?.advisers || []).map((user: any) => ({
        name: user.full_name,
        value: user.id
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advisor]);

  useEffect(() => {
    const currentAdvisers = advisor?.advisers || [];
    setDefaultAdviserId(
      advisor.access_level === 'Adviser'
        ? advisor.id
        : currentAdvisers.length === 1
          ? currentAdvisers[0].id
          : null
    );
  }, [mappedAdvisers]);

  return (
    <Formik
      enableReinitialize
      initialValues={{
        name: caseName,
        customers: importedCustomers,
        adviser_id: defaultAdviserId
      }}
      validationSchema={validationSchema}
      onSubmit={(values, { resetForm }) => {
        onSubmit(values);
        resetForm();
      }}
    >
      {({
        handleSubmit,
        values,
        isSubmitting,
        resetForm
      }: FormikProps<any>) => {
        const { customers } = values;
        const isAnyCustomer = customers.length > 0;
        const customersCount = customers.length;
        const isMaxCustomers = customersCount > 1;

        return (
          <BoxWrapper title="New case" closeModal={() => close(resetForm)}>
            <form onSubmit={handleSubmit}>
              <Grid
                container
                direction="column"
                justify="space-between"
                className={classes.content}
              >
                <div
                  style={{
                    marginBottom: '1rem'
                  }}
                >
                  <Field
                    label="Case name"
                    name="name"
                    component={Fields.v2.Text}
                    InputProps={{
                      onChange: (e: any) => setCaseName(e, values.customers)
                    }}
                  />
                </div>
                {mappedAdvisers.length > 1 ? (
                  <div
                    style={{
                      marginBottom: '1rem'
                    }}
                  >
                    <Field
                      name="adviser_id"
                      component={Fields.v2.Select}
                      label="Adviser"
                      fullWidth
                      options={mappedAdvisers}
                    />
                  </div>
                ) : (
                  <></>
                )}
                <FieldArray name="customers" component={Customers} />
                {!isMaxCustomers && (
                  <div className={classes.btnContainer}>
                    {showIoImport && (
                      <img
                        alt={'intelliflo import'}
                        src={intellifloLogo}
                        height={'22px'}
                        style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => {
                          if (isAuthenticated) {
                            // SHOW MODAL WITH CLIENT SEARCH
                            return setIsImportUserModal();
                          } else {
                            // Redirect to login
                            return signIn({ action: Action.IMPORT_CLIENT });
                          }
                        }}
                      />
                    )}

                    <Button
                      className={classes.addCustomer}
                      small
                      color="primary"
                      data-id="addClient"
                      disabled={isMaxCustomers}
                      onClick={() => {
                        importedCustomers[0].first_name =
                          customers[0].first_name;
                        importedCustomers[0].last_name = customers[0].last_name;
                        importedCustomers.splice(1);
                        setImportedCustomers([
                          ...importedCustomers,
                          {
                            first_name: '',
                            last_name: ''
                          }
                        ]);
                      }}
                    >
                      Add client
                    </Button>
                  </div>
                )}
                <Grid item xs={12} style={{ marginTop: 'auto' }}>
                  <Button
                    isLoading={isSubmitting}
                    disabled={!isAnyCustomer}
                    type="submit"
                    data-id="createCase"
                    style={{
                      marginTop: '2rem',
                      width: '100%'
                    }}
                  >
                    Create case
                  </Button>
                </Grid>
              </Grid>
              <Persist name="new-case" debounce={100} />
            </form>
          </BoxWrapper>
        );
      }}
    </Formik>
  );
};

export default compose(
  connect((state: any) => ({
    advisor: selectors.advisorSelectors.getAdvisor(state)
  }))
)(NewCase);
